'use client';
import { useState, useCallback } from 'react';
import { AttachedFile, FloraAICredits, FloraLibraryDocument } from '../types/flora-ai.types';
import {
	mockAttachedFiles,
	mockCreditsData,
	mockAdminCreditsData,
	mockLibraryFilters,
	mockLibraryDocuments,
} from '../services';
import { UserRole } from '@practium/shared';

interface UseFloraAIProps {
	userRole?: UserRole;
}

export const useFloraAI = ({ userRole }: UseFloraAIProps = {}) => {
	const isAdmin = userRole === UserRole.Admin;

	const [attachedFiles] = useState<AttachedFile[]>(mockAttachedFiles);
	const [credits] = useState<FloraAICredits>(isAdmin ? mockAdminCreditsData : mockCreditsData);
	const [isLibraryOpen, setIsLibraryOpen] = useState(false);
	const [activeLibraryFilterId, setActiveLibraryFilterId] = useState(mockLibraryFilters[0]?.id ?? '');
	const [libraryDocuments, setLibraryDocuments] = useState<FloraLibraryDocument[]>(mockLibraryDocuments);
	const [selectedLibraryIds, setSelectedLibraryIds] = useState<string[]>([]);

	const handleSendMessage = useCallback((_message: string) => {}, []);

	const handleAttach = useCallback(() => {}, []);

	const handleDeepThink = useCallback(() => {}, []);

	const handleSuggestionClick = useCallback((_label: string) => {}, []);

	const handleViewChats = useCallback(() => {}, []);

	const handleUpgrade = useCallback(() => {}, []);

	const handleOpenLibrary = useCallback(() => setIsLibraryOpen(true), []);

	const handleCloseLibrary = useCallback(() => {
		setIsLibraryOpen(false);
		setSelectedLibraryIds([]);
	}, []);

	const handleLibrarySearch = useCallback((_query: string) => {}, []);

	const handleLibraryUpload = useCallback(() => {}, []);

	const handleToggleLibrarySelection = useCallback((documentId: string) => {
		setSelectedLibraryIds((prev) =>
			prev.includes(documentId) ? prev.filter((id) => id !== documentId) : [...prev, documentId],
		);
	}, []);

	const handleLibraryRemoveSelected = useCallback(() => {
		setLibraryDocuments((prev) => prev.filter((document) => !selectedLibraryIds.includes(document.id)));
		setSelectedLibraryIds([]);
	}, [selectedLibraryIds]);

	const handleLibraryAddSelected = useCallback(() => {
		setLibraryDocuments((prev) =>
			prev.map((document) =>
				selectedLibraryIds.includes(document.id) ? { ...document, inDossier: true } : document,
			),
		);
		setSelectedLibraryIds([]);
	}, [selectedLibraryIds]);

	const handleToggleLibraryDocument = useCallback((documentId: string) => {
		setLibraryDocuments((prev) =>
			prev.map((document) =>
				document.id === documentId ? { ...document, inDossier: !document.inDossier } : document,
			),
		);
	}, []);

	return {
		attachedFiles,
		credits,
		isAdmin,
		isLibraryOpen,
		activeLibraryFilterId,
		selectedLibraryIds,
		selectedLibraryCount: selectedLibraryIds.length,
		libraryFilters: mockLibraryFilters,
		libraryDocuments,
		handlers: {
			handleSendMessage,
			handleAttach,
			handleDeepThink,
			handleSuggestionClick,
			handleViewChats,
			handleUpgrade,
			handleOpenLibrary,
			handleCloseLibrary,
			handleLibraryFilterChange: setActiveLibraryFilterId,
			handleLibrarySearch,
			handleLibraryUpload,
			handleLibraryRemoveSelected,
			handleLibraryAddSelected,
			handleToggleLibraryDocument,
			handleToggleLibrarySelection,
		},
	};
};
